import { 
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  getDocs,
  query,
  where,
  deleteDoc,
  runTransaction
} from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { UserData } from '@/types/user';
import { EvaluatorInput, Evaluator } from '@/types/evaluator';
import { validateEvaluatorChanges } from './evaluatorValidationService';

// Get user's original email
export async function getOriginalEmail(userId: string): Promise<string | null> {
  if (!userId) throw new Error('User ID is required');

  try {
    const userRef = doc(db, collections.users, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    return userData.originalEmail || userData.email || null;
  } catch (error: any) {
    console.error('Error fetching user email:', error);
    throw new Error('Failed to load user email');
  }
}

// Helper function to generate evaluator document ID
function generateEvaluatorDocId(evaluatorId: string, userId: string): string {
  return `evaluator_${evaluatorId}_${userId}`;
}

// User-related functions
export async function getUserData(userId: string): Promise<UserData> {
  if (!userId) throw new Error('User ID is required');

  try {
    const userRef = doc(db, collections.users, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    return {
      id: userDoc.id,
      ...userDoc.data()
    } as UserData;
  } catch (error: any) {
    console.error('Error fetching user data:', error);
    throw new Error('Failed to load user data. Please sign in again.');
  }
}

export async function updateUserProfile(userId: string, data: Partial<UserData>): Promise<void> {
  if (!userId) throw new Error('User ID is required');

  try {
    const userRef = doc(db, collections.users, userId);
    await updateDoc(userRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error: any) {
    console.error('Error updating user profile:', error);
    throw new Error('Failed to update profile');
  }
}

// Evaluator-related functions
export async function getEvaluatorsByUserId(userId: string): Promise<Evaluator[]> {
  if (!userId) throw new Error('User ID is required');

  try {
    const evaluatorsRef = collection(db, collections.evaluators);
    const q = query(evaluatorsRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id.split('_')[1], // Extract original evaluator ID
      ...doc.data()
    })) as Evaluator[];
  } catch (error: any) {
    console.error('Error fetching evaluators:', error);
    throw new Error('Failed to load evaluators');
  }
}

export async function createEvaluator(userId: string, data: EvaluatorInput): Promise<void> {
  if (!userId) throw new Error('User ID is required');

  try {
    const batch = writeBatch(db);
    const evaluatorsRef = collection(db, collections.evaluators);
    
    // Check for existing evaluator with same email
    const q = query(evaluatorsRef, 
      where('userId', '==', userId),
      where('email', '==', data.email.toLowerCase())
    );
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      throw new Error('An evaluator with this email already exists');
    }

    // Generate a unique evaluator ID
    const evaluatorId = doc(collection(db, 'temp')).id;
    const combinedDocId = generateEvaluatorDocId(evaluatorId, userId);
    const newEvaluatorRef = doc(evaluatorsRef, combinedDocId);

    // Create new evaluator document
    batch.set(newEvaluatorRef, {
      id: evaluatorId,
      userId,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email.toLowerCase().trim(),
      contextualized: false,
      evaluationCompleted: false,
      lastReminderSentAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Update user's evaluator count
    const userRef = doc(db, collections.users, userId);
    const userData = await getDoc(userRef);
    
    if (userData.exists()) {
      const currentCount = userData.data().evaluatorsCount || 0;
      batch.update(userRef, {
        evaluatorsCount: currentCount + 1,
        evaluatorsNominated: true,
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
  } catch (error: any) {
    console.error('Error creating evaluator:', error);
    throw new Error(error.message || 'Failed to create evaluator');
  }
}

export async function updateEvaluator(evaluatorId: string, userId: string, data: EvaluatorInput): Promise<void> {
  if (!evaluatorId || !userId) throw new Error('Evaluator ID and User ID are required');

  try {
    const combinedDocId = generateEvaluatorDocId(evaluatorId, userId);
    const evaluatorRef = doc(db, collections.evaluators, combinedDocId);
    const evaluatorDoc = await getDoc(evaluatorRef);

    if (!evaluatorDoc.exists()) {
      throw new Error('Evaluator not found');
    }

    await updateDoc(evaluatorRef, {
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      updatedAt: serverTimestamp()
    });
  } catch (error: any) {
    console.error('Error updating evaluator:', error);
    throw new Error(error.message || 'Failed to update evaluator');
  }
}

export async function deleteEvaluator(evaluatorId: string, userId: string): Promise<void> {
  if (!evaluatorId || !userId) {
    throw new Error('Evaluator ID and User ID are required');
  }

  try {
    const batch = writeBatch(db);
    const combinedDocId = generateEvaluatorDocId(evaluatorId, userId);
    
    // Delete the evaluator document
    const evaluatorRef = doc(db, collections.evaluators, combinedDocId);
    batch.delete(evaluatorRef);

    // Update user's evaluator count
    const userRef = doc(db, collections.users, userId);
    const userData = await getDoc(userRef);
    
    if (userData.exists()) {
      const currentCount = userData.data().evaluatorsCount || 0;
      batch.update(userRef, {
        evaluatorsCount: Math.max(0, currentCount - 1),
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
  } catch (error: any) {
    console.error('Error deleting evaluator:', error);
    throw new Error('Failed to delete evaluator');
  }
}

export async function finalizeEvaluators(userId: string): Promise<void> {
  if (!userId) throw new Error('User ID is required');

  try {
    const userRef = doc(db, collections.users, userId);
    await updateDoc(userRef, {
      evaluatorsFinalized: true,
      updatedAt: serverTimestamp()
    });
  } catch (error: any) {
    console.error('Error finalizing evaluators:', error);
    throw new Error('Failed to finalize evaluators');
  }
}

export async function migrateEvaluatorDocuments(): Promise<void> {
  try {
    const evaluatorsRef = collection(db, collections.evaluators);
    const snapshot = await getDocs(evaluatorsRef);
    
    const batch = writeBatch(db);
    let operationCount = 0;

    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (!data.userId) continue;

      // Generate new document ID
      const newDocId = generateEvaluatorDocId(doc.id, data.userId);
      const newDocRef = doc(evaluatorsRef, newDocId);

      // Copy data to new document
      batch.set(newDocRef, {
        ...data,
        id: doc.id, // Preserve original ID as a field
        updatedAt: serverTimestamp()
      });

      // Delete old document
      batch.delete(doc.ref);

      operationCount++;
      
      // Commit batch every 500 operations
      if (operationCount >= 500) {
        await batch.commit();
        operationCount = 0;
      }
    }

    // Commit any remaining operations
    if (operationCount > 0) {
      await batch.commit();
    }
  } catch (error: any) {
    console.error('Error migrating evaluator documents:', error);
    throw new Error('Failed to migrate evaluator documents');
  }
}